import React, { useCallback, useMemo, useState } from "react";
import { cloneDeep, set, get, last } from "lodash";
import { useForm } from "react-hook-form";

import useTranslations from "../../hooks/useTranslations";
import {
  AnalyticsIcon,
  BriefcaseIcon,
  CollectionIcon,
  MenuIcon as TextLongIcon,
  SelectorMultipleIcon,
  SelectorSingleIcon,
  StarIcon,
  TextShortIcon,
} from "../icons";
import { H3 } from "../wombat-ui";

import {
  Menu,
  MenuList,
  MenuGroup,
  MenuItem,
  Text,
} from "@chakra-ui/react";

import { JobSectionModal } from "./JobSectionModal";
import { QuestionFormInput } from "./QuestionWithOptionsForm";
import { SectionFormInput } from "./SectionForm";
import useViewer from "../../hooks/useViewer";

type TemplateFormElementMenuProps = React.PropsWithChildren<{
  insideSection: boolean;
  getValues: ReturnType<typeof useForm>["getValues"];
  setValue: ReturnType<typeof useForm>["setValue"];
  fields: any;
  append?: any;
  insert?: any;
  reset?: ReturnType<typeof useForm>["reset"];
  sectionIndex?: number;
  handleFocus: (sectionIndex: number, question: number) => void;
  name: string;
  insertIndex?: number;
}>;

const getDefaultSectionAttribute = ({
  index = 0,
}): Partial<SectionFormInput> => ({
  order: index,
  title: "",
  description: "",
});

export const getDefaultQuestionAttribute = ({
  index = 0,
  type,
  options,
}): Partial<QuestionFormInput> => ({
  order: index,
  label: "",
  options,
  reportTypes: ["FirstReport", "IntermediateReport", "FinalReport"],
  visibility: "all",
  required: true,
  type,
  documents: []
});

const TemplateFormElementMenu: React.FC<TemplateFormElementMenuProps> = ({
  insideSection,
  getValues,
  setValue,
  fields,
  sectionIndex,
  handleFocus,
  reset,
  name,
  insertIndex = -1,
  children,
}) => {
  const t = useTranslations();
  const [jobSectionModalOpen, setJobSectionModalOpen] = useState(false);
  const {
    company: { questionOptionsConfigurations },
    locale,
  } = useViewer();

  const questionOptionsConfiguration = questionOptionsConfigurations.find(
    (a) => a.locale === locale
  );

  const sectionTypes = useMemo(
    () => [
      {
        label: t("templates.sectionType.simple.label"),
        value: "SectionSimple",
        description: t("templates.sectionType.simple.description"),
        icon: CollectionIcon,
      },
      {
        label: t("templates.sectionType.job.label"),
        value: "SectionJob",
        description: t("templates.sectionType.job.description"),
        icon: BriefcaseIcon,
      },
    ],
    []
  );

  const questionTypes = useMemo(
    () => [
      {
        label: t("templates.answerType.skillEvaluation.label"),
        description: t("templates.answerType.skillEvaluation.description"),
        value: "QuestionSkill",
        skillId: "",
        options:
          questionOptionsConfiguration.skillQuestionOptionsConfiguration.map(
            (e) => ({
              value: e.value,
              label: e.label,
            })
          ),
        icon: AnalyticsIcon,
      },
      {
        label: t("templates.answerType.text.label"),
        description: t("templates.answerType.text.description"),
        value: "QuestionText",
        options: null,
        icon: TextLongIcon,
      },
      {
        label: t("templates.answerType.selectorSingle.label"),
        description: t("templates.answerType.selectorSingle.description"),
        value: "QuestionSelectorSingle",
        options: [],
        icon: SelectorSingleIcon,
      },
      {
        label: t("templates.answerType.selectorMultiple.label"),
        description: t("templates.answerType.selectorMultiple.description"),
        value: "QuestionSelectorMultiple",
        options: [],
        icon: SelectorMultipleIcon,
      },
      {
        label: t("templates.answerType.evaluation.label"),
        description: t("templates.answerType.evaluation.description"),
        value: "QuestionEvaluation",
        options:
          questionOptionsConfiguration.evaluationQuestionOptionsConfiguration.map(
            (e) => ({
              value: e.value,
              label: e.label,
            })
          ),
        icon: StarIcon,
      },
    ],
    []
  );

  const addQuestion = useCallback(
    (value: string, options: any) => {
      const values = cloneDeep(getValues());

      const questionsAttributesName = `${name}.questionsAttributes`;
      const questionsAttributes = get(values, questionsAttributesName) || [];

      questionsAttributes.splice(
        insertIndex === -1 ? questionsAttributes.length : insertIndex,
        0,
        getDefaultQuestionAttribute({
          type: value,
          index: insertIndex === -1 ? fields.length : insertIndex,
          options,
        })
      );

      set(values, questionsAttributesName, questionsAttributes);

      reset(values);

      handleFocus(
        sectionIndex,
        insertIndex === -1 ? fields.length : insertIndex
      );
    },
    [
      getValues,
      handleFocus,
      sectionIndex,
      fields.length,
      reset,
      name,
      insertIndex,
    ]
  );

  const addSection = useCallback(
    (value: string) => {
      switch (value) {
        case "SectionSimple": {
          const values = cloneDeep(getValues());
          const sectionsAttributesName = `${name}.sectionsAttributes`;
          const sectionsAttributes =
            get(values, sectionsAttributesName) || [];
          sectionsAttributes
            .splice(
              insertIndex === -1
                ? sectionsAttributes.length
                : insertIndex,
              0,
              {
                ...getDefaultSectionAttribute({
                  index: insertIndex === -1 ? fields.length : insertIndex,
                }),
              }
            )
            .map((i, index) => ({ ...i, index }));

          set(
            values,
            sectionsAttributesName,
            sectionsAttributes
          );

          reset(values);
          return;
        }
        case "SectionJob":
          setJobSectionModalOpen(true);
          return;
        default:
          return null;
      }
    },
    [sectionTypes, fields.length, getValues, setValue, insertIndex]
  );

  return (
    <>
      <Menu preventOverflow placement="auto">
        {children}
        <MenuList>
          {!insideSection && (
            <>
              <MenuGroup title={"Sections"}>
                {sectionTypes.map(({ label, value, description, icon }) => (
                  <MenuItem
                    onClick={() => {
                      addSection(value);
                    }}
                    icon={React.createElement(icon, { className: "w-4 h-4" })}
                    key={value}
                    data-test-id={`sectionButton${value}`}
                  >
                    {label}
                    <Text fontSize="xs">{description}</Text>
                  </MenuItem>
                ))}
              </MenuGroup>
            </>
          )}
          {insideSection && (
            <MenuGroup title={"Questions"}>
              {questionTypes.map(
                ({ label, value, description, icon, options }) => (
                  <MenuItem
                    onClick={() => {
                      addQuestion(value, options);
                    }}
                    icon={React.createElement(icon, { className: "w-4 h-4" })}
                    key={value}
                    data-test-id={`questionButton${value}`}
                  >
                    {label}
                    <Text fontSize="xs">{description}</Text>
                  </MenuItem>
                )
              )}
            </MenuGroup>
          )}
        </MenuList>
      </Menu>
      <JobSectionModal
        open={jobSectionModalOpen}
        onClose={() => {
          setJobSectionModalOpen(false);
        }}
        getValues={getValues}
        setValue={setValue}
        fields={fields}
        insertIndex={insertIndex}
        reset={reset}
        name={name}
      />
    </>
  );
};

export default TemplateFormElementMenu;
